/**
 * Exam tab — past papers to download as worksheets. WP6 rebuild against
 * prototypes/exam.html, with the wireframe's bordered page-head superseded
 * by the Learn/Children chrome (user decision 2026-08-06 — the wireframe was
 * signed off at WP0, before WP3 settled the page scaffold).
 *
 * Syllabus/Paper type/Year picker fields (WP1), KSSM selected by default —
 * defaulting to KSSR landed every parent on an empty list as if a filter had
 * produced it (PEXM-012).
 *
 * KSSR is NOT locked. An earlier pass gave it the coming-soon lock, which was
 * wrong: `syllabus_catalog` puts KSSR and KSSM both on `standard`, so no plan
 * gates it. Its list is empty only because no KSSR papers have been loaded
 * yet, and a padlock claims "pay to unlock" — a different, false statement.
 * The empty state says which of the two it is instead.
 *
 * The syllabus that IS tier-gated is IGCSE (premium). It stays browsable
 * here on purpose: decision 32 keeps lists ungated, and the server rejects
 * the download with `upgrade_required`.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useRef } from 'react';
import { capture } from '@tupai/analytics/client';
import { getExams } from '@/lib/api';
import type { ExamSummary } from '@/lib/api';
import { useWorksheetStore } from '@/store/worksheet-store';
import { useExamFilterStore } from '@/store/exam-filter-store';
import { useStuck } from '@/lib/use-stuck';
import GateNoticeModal from '@/components/gate-notice-modal';
import LanguageSheet from '@/components/language-sheet';
import PickerField from '@/components/picker-field';
import { IconFileText } from '@tabler/icons-react';
import TrialBanner from '@/components/trial-banner';
import reading from '@tupai/tokens/assets/spot/reading.svg';
import { useTranslation } from '@tupai/i18n/react';

export const Route = createFileRoute('/_protected/exam')({
  component: WorksheetPage,
});

const SYLLABUSES = ['KSSM', 'IGCSE', 'KSSR'] as const;

function WorksheetPage() {
  const {
    downloadingExamId,
    langMenuOpenFor,
    setLangMenuOpenFor,
    downloadExamWorksheet,
    gateCode,
    setGateCode,
  } = useWorksheetStore();
  const { t } = useTranslation('work');

  // Shared with ExamChildRailCard (WP1) — see store/examFilterStore.ts for
  // the picker-wins-over-rail-selection rule.
  const { syllabus: examSyllabus, setSyllabus: setExamSyllabus } = useExamFilterStore();
  const [selectedExamYear, setSelectedExamYear] = useState<'All' | number>('All');
  const [selectedPaperType, setSelectedPaperType] = useState<'All' | string>('All');
  const [dbExams, setDbExams] = useState<ExamSummary[]>([]);
  // The list loads on open, so start as loading: no "0 papers" before it arrives (TUP-1792).
  const [loadingExams, setLoadingExams] = useState(true);

  // `/exam` renders this page (router.tsx) — "browsed" fires once per mount,
  // never per syllabus/year filter tap, which is why it's not in the
  // getExams effect below. No specific paper is being viewed here, so
  // paper_id is left off rather than guessed at.
  const browsedRef = useRef(false);
  useEffect(() => {
    if (browsedRef.current) return;
    browsedRef.current = true;
    capture('exam_browsed', {});
  }, []);

  useEffect(() => {
    setLoadingExams(true);
    setSelectedExamYear('All');
    // KSSM has TRIAL/PAST, IGCSE has CORE/EXTENDED — a held paper-type value
    // would match nothing across a syllabus switch.
    setSelectedPaperType('All');
    getExams(examSyllabus)
      .then(setDbExams)
      .catch((err) => {
        console.error(err);
        setDbExams([]);
      })
      .finally(() => setLoadingExams(false));
  }, [examSyllabus]);

  // Years are derived from the catalog itself (exam/papers.tsx does the same
  // in the student app) rather than hardcoded -- a fixed list silently hides
  // whatever years fall outside it as the papers table grows.
  const availableYears = useMemo(
    () => [
      'All' as const,
      ...Array.from(new Set(dbExams.map((ex) => ex.year))).sort((a, b) => b - a),
    ],
    [dbExams]
  );

  // Same derivation, for the paper type ('TRIAL' | 'PAST' | 'CORE' |
  // 'EXTENDED') already on every row — no separate endpoint needed.
  const availablePaperTypes = useMemo(
    () => ['All' as const, ...Array.from(new Set(dbExams.map((ex) => ex.tag)))],
    [dbExams]
  );
  // Same threshold the student app uses (exam/papers.tsx) for its own
  // paper-type filter: hide the field entirely when the catalog has only
  // one tag to filter by.
  const showPaperType = availablePaperTypes.length > 2;

  const filteredExams = dbExams.filter(
    (ex) =>
      (selectedExamYear === 'All' || ex.year === selectedExamYear) &&
      (selectedPaperType === 'All' || ex.tag === selectedPaperType)
  );

  const sheetExam = dbExams.find((ex) => ex.id === langMenuOpenFor);

  // Stuck detection for the cream filter band below — see lib/useStuck.ts.
  // `rootRef` goes on this page's own scroll column (the nearest scrolling
  // ancestor `sticky` resolves against, per the comment above it), and
  // `sentinelRef` on the 1x1px node inside the plain `relative` wrapper
  // around the sticky band (not on the sticky element itself — see the
  // comment at that wrapper for why).
  const { rootRef, sentinelRef, stuck } = useStuck<HTMLDivElement, HTMLDivElement>();

  return (
    // White page with the title in flow, per ChildrenPage / student learn.tsx.
    <div className="flex-1 flex flex-col min-h-0 bg-surface">
      <div ref={rootRef} className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-3">
        {/* `pt-5` used to live on the scroll column itself. `sticky top-0`
            below resolves against the column's PADDING edge, which sits
            below that padding — so once the band was stuck, a ~20px strip
            of the column's own top padding sat above the band's opaque
            backdrop, and a scrolling card showed through that strip
            (confirmed in the running app: sticky's stuck offset landed at
            containerTop + 20px, never at containerTop, no matter what
            margin the sticky wrapper itself carried — Chromium computes the
            stuck position from the padding edge and does not let a
            negative margin on the sticky element pull it past that edge,
            unlike the `-mx-4`/`px-4` horizontal trick below, which works
            precisely because horizontal margin doesn't feed into a
            column-direction flex gap the way vertical margin does). The fix
            is to give the column zero top padding and put that `pt-5` here
            instead, on the first (non-sticky) child — same visual gap above
            the title, but now the column's padding edge coincides with its
            border edge, so `sticky top-0` pins the band flush with the
            column's actual top and there's no padding strip left for
            anything to show through. */}
        <div className="pt-5">
          <h1 className="font-display text-2xl text-content">{t('examTitle')}</h1>
        </div>

        <TrialBanner />

        {/* Sticky filter band: this scroll column is the page's own
            `flex-1 overflow-y-auto` region (AppShell gets `scroll={false}`
            in router.tsx precisely so this div, not AppShell's `main`, is
            the nearest scrolling ancestor `sticky` resolves against) — so
            `sticky top-0` pins the band to the top of THIS column, not the
            window, and lets the `h1` above scroll away as intended.
            The band itself (`rounded-lg bg-bg`) is narrower than this
            column's own width once you count the rounded corners, so an
            opaque `bg-surface` matching the page underneath it would still
            leave slivers at the two top corners for a scrolling card to
            show through. Rather than trust the corner math, this wrapper
            breaks out of the column's `px-4` with matching negative margin
            (`-mx-4`) and reapplies it as its own padding, so the opaque
            backdrop spans the column edge-to-edge — no gap beside the band
            for anything to peek through, at any width, corners included.
            (The vertical equivalent of this same padding leak — the column's
            `pt-5` — can't be closed the same way here; see the comment on
            the column's first child above for why the fix moved there
            instead of onto this wrapper.)
            z-30: above the plain cards below (unstyled, default stacking)
            and above Nav's z-10 raised disc, but under SheetDialog/
            SideDrawer's z-60 (LinkChildSheet, GateNoticeModal, the
            BottomSheet PickerField opens below `lg`) so a sheet still
            covers it. PickerField's own `lg` dropdown (z-20, absolute
            inside the band) still renders above the band and the cards
            regardless of this z-30 vs. its z-20 — it's a descendant of the
            band's own stacking context, painted after the band's other
            content within that same context, not competing with cards on
            the page's root stacking order. */}
        {/* useStuck sentinel: a sibling BEFORE the sticky div, not its
            parent — wrapping the sticky div in this wrapper made the
            wrapper (sized to fit its content, i.e. the sticky child alone)
            the sticky element's containing block, leaving it zero room to
            travel before hitting that block's bottom edge, so it unpinned
            on the very first pixel of scroll. As a flow sibling instead,
            this "relative h-0" wrapper doesn't touch the sticky div's
            containing block (still the full scroll column below), while
            still giving the 1x1px sentinel inside it something non-sticky
            to move through the flow with — see lib/useStuck.ts. */}
        <div className="relative h-0">
          <div ref={sentinelRef} aria-hidden="true" className="absolute left-0 top-0 h-px w-px" />
        </div>

        <div className="sticky top-0 z-30 -mx-4 px-4 bg-surface">
          {/* Picker fields replace both chip rows (WP1), following the same
                recipe as PracticePage's own field row — label sits right of the
                fields, bottom-aligned, from md — but without PracticePage's
                `md:max-w-[440px]` cap: this row can hold three fields (Syllabus,
                Paper type, Year) rather than two, so it needs the room. Wrapped
                in a cream inset band (`bg` = "inset row", DESIGN.md 140) so the
                white PickerField buttons read against cream instead of
                disappearing into the white page. */}
          <div
            className={`bg-bg p-3 flex flex-col gap-2 transition-all md:flex-row md:items-end md:justify-between md:gap-3 ${
              stuck ? 'rounded-none' : 'rounded-lg'
            }`}
          >
            <div className="grid grid-cols-2 gap-2 md:flex md:flex-1 md:min-w-0">
              <PickerField
                caption={t('syllabusFieldLabel')}
                displayValue={examSyllabus}
                value={examSyllabus}
                options={SYLLABUSES.map((sy) => ({ value: sy, label: sy }))}
                onChange={(v) => setExamSyllabus(v as (typeof SYLLABUSES)[number])}
                sheetTitle={t('syllabusFieldLabel')}
                sheetSubtitle={t('chooseSyllabus')}
              />

              {showPaperType && (
                <PickerField
                  caption={t('paperTypeFieldLabel')}
                  displayValue={
                    selectedPaperType === 'All'
                      ? t('allPaperTypes')
                      : t('paperTypeLabel', { tag: selectedPaperType })
                  }
                  value={selectedPaperType}
                  options={availablePaperTypes.map((tag) => ({
                    value: tag,
                    label: tag === 'All' ? t('allPaperTypes') : t('paperTypeLabel', { tag: tag }),
                  }))}
                  onChange={setSelectedPaperType}
                  sheetTitle={t('paperTypeFieldLabel')}
                  sheetSubtitle={t('choosePaperType')}
                />
              )}

              {/* Full second row at phone widths when the paper type field is
                also showing — three fields don't fit one row at 360. With
                the paper type field hidden this collapses back to col-span-1
                so Syllabus and Year sit side by side. */}
              <div
                className={
                  showPaperType
                    ? 'col-span-2 md:col-span-1 md:flex-1 md:min-w-0'
                    : 'md:flex-1 md:min-w-0'
                }
              >
                <PickerField
                  caption={t('yearFieldLabel')}
                  displayValue={
                    selectedExamYear === 'All' ? t('allYears') : String(selectedExamYear)
                  }
                  value={String(selectedExamYear)}
                  options={availableYears.map((yr) => ({
                    value: String(yr),
                    label: yr === 'All' ? t('allYears') : String(yr),
                  }))}
                  onChange={(v) => setSelectedExamYear(v === 'All' ? 'All' : Number(v))}
                  sheetTitle={t('yearFieldLabel')}
                  sheetSubtitle={t('chooseYear')}
                />
              </div>
            </div>

            <p className="text-xs font-bold text-muted uppercase tracking-wide whitespace-nowrap">
              {loadingExams ? null : t('paperCount', { n: filteredExams.length })}
            </p>
          </div>
        </div>

        {loadingExams ? (
          <div className="flex items-center justify-center py-10">
            <div className="size-6 border-2 border-primary border-t-transparent rounded-pill animate-spin" />
          </div>
        ) : filteredExams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center gap-3 bg-surface rounded-lg border border-border">
            <img src={reading} alt="" className="size-10 animate-bounce" />
            <div>
              <p className="text-sm font-bold text-content">
                {dbExams.length === 0
                  ? t('papersOnTheWay', { syllabus: examSyllabus })
                  : t('noPapersYet')}
              </p>
              {/* Three empty states, and saying which is which is the whole
                  point: nothing loaded for this syllabus yet, versus a paper
                  type filter that matched nothing (year may or may not also
                  be narrowing — the type-specific message covers both), versus
                  a year-only filter that matched nothing. */}
              <p className="mt-1 text-sm text-muted max-w-[220px] mx-auto leading-normal">
                {dbExams.length === 0
                  ? t('stillAddingThem', {
                      others: SYLLABUSES.filter((sy) => sy !== examSyllabus).join(' / '),
                    })
                  : selectedPaperType !== 'All'
                    ? t('noPapersMatchFilters', { syllabus: examSyllabus })
                    : t('noPapersFrom', { syllabus: examSyllabus, year: String(selectedExamYear) })}
              </p>
            </div>
          </div>
        ) : (
          // One tree: the same card at every width. Phone is one column;
          // from md the two halves (first ceil(n/2) papers, then the rest)
          // sit side by side as two columns of cards, reading top-to-bottom
          // then left-to-right — mirrors PracticePage's chapter split exactly.
          <div className="flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-3 md:items-start">
            {[
              filteredExams.slice(0, Math.ceil(filteredExams.length / 2)),
              filteredExams.slice(Math.ceil(filteredExams.length / 2)),
            ].map((group, gi) => (
              <div key={gi} className="flex flex-col gap-2 md:gap-3">
                {group.map((ex) => (
                  // The whole card is the affordance, matching PracticePage's
                  // chapter card (lift on hover, settle on press) — a flat card
                  // with one small link in its footer read as static, and on a
                  // 672px column that link sat a long way from the title it
                  // belongs to. Tapping opens the language sheet, which is the
                  // confirmation step, so nothing downloads by accident.
                  // The disable is global (the store tracks one download at a
                  // time) but only the card actually fetching dims — fading
                  // all 54 made the whole list look broken for one download.
                  <button
                    key={ex.id}
                    type="button"
                    disabled={downloadingExamId !== null}
                    onClick={() => setLangMenuOpenFor(ex.id)}
                    className={`w-full text-left bg-surface rounded-lg border border-border p-4 shadow-sm transition-all enabled:hover:shadow-md active:scale-[.99] ${
                      downloadingExamId === ex.id ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-pill bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <IconFileText size={18} stroke={2} aria-hidden />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-content leading-snug">{ex.title}</p>
                        <p className="mt-0.5 text-sm text-muted">
                          {[t('questionCount', { n: ex.questions }), ex.time, ex.variant, ex.series]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center border-t border-border-light mt-2.5 pt-2.5">
                      <span className="text-sm font-bold text-muted">
                        {[t('paperTypeLabel', { tag: ex.tag }), ex.form]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {/* A label now, not a control: the card itself carries
                          the action, and a button inside a button is invalid. */}
                      <span className="flex min-h-11 items-center px-2 text-sm font-bold text-primary">
                        {downloadingExamId === ex.id ? t('downloading') : t('download')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <LanguageSheet
        open={sheetExam !== undefined}
        onClose={() => setLangMenuOpenFor(null)}
        title={sheetExam?.title ?? ''}
        onPick={(lang) => sheetExam && downloadExamWorksheet(sheetExam, lang)}
      />

      {gateCode && <GateNoticeModal code={gateCode} onClose={() => setGateCode(null)} />}
    </div>
  );
}
